import React, { useState } from 'react';
import { TimePicker } from '@mui/x-date-pickers';
import { SxProps, ThemeProvider, useTheme } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import moment from 'moment';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData, HolidayCalendar } from '../../api';
import { getTimeFormat } from '../getDateAndTimeFormat';
import { cacheLtr } from '../GeneralUtils';

interface SharedMobileTimePickerProps {
  // Core required props
  value: moment.Moment | number; // Support both moment and unix timestamp
  onChange: (newValue: moment.Moment | null) => void;
  label?: string | JSX.Element;

  // Time constraints
  minTime?: moment.Moment;
  maxTime?: moment.Moment;
  minutesStep?: number; // default: 15

  // Form integration
  name?: string;
  disabled?: boolean;
  format?: string; // For FormTimePicker timeFormat support

  // Styling & behavior
  sx?: SxProps;
  size?: 'small' | 'medium';
  showUpdatedStyling?: boolean; // Green border for FormTimePicker
  onAccept?: (value: moment.Moment | null) => void;

  // Formik support
  field?: any; // For {...field} spreading
}

export const SharedMobileTimePicker = ({
  value,
  onChange,
  label,
  minTime,
  maxTime,
  minutesStep = 15,
  name,
  disabled,
  format,
  sx,
  size,
  showUpdatedStyling = false,
  onAccept,
  field,
}: SharedMobileTimePickerProps) => {
  const theme = useTheme();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const [open, setOpen] = useState(false);

  // Auto-detect ampm format from account settings
  const ampm = account.holiday_calendar === HolidayCalendar.US;

  // Auto-detect time format if not provided
  const timeFormat = format || getTimeFormat(account);

  // Convert value to moment object
  const momentValue = typeof value === 'number' ? moment.unix(value).tz('utc') : value;

  // Style for FormTimePicker green border when field is updated
  const updatedStyling = showUpdatedStyling
    ? {
        '&& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
          borderWidth: '4px',
        },
        '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
          borderWidth: '4px',
        },
      }
    : {};

  // sx targets the TextField root, so it can only style the trigger, not the popup.
  const combinedSx = {
    ...updatedStyling,
    ...sx,
  };

  // Styles for the portaled popup: force LTR so hour|minute|ampm read left→right
  // (both the toolbar time label and the digital-clock columns).
  const popupSx = {
    direction: 'ltr',
    '& .MuiPickersToolbar-content, & .MuiTimePickerToolbar-hourMinuteLabel': {
      direction: 'ltr',
      flexDirection: 'row !important',
    },
    '& .MuiTimePickerToolbar-root, & .MuiTimeClock-root': {
      direction: 'ltr',
    },
    '& .MuiMultiSectionDigitalClock-root, & [class*="MuiMultiSectionDigitalClock-root"]': {
      direction: 'ltr !important',
      display: 'flex',
      flexDirection: 'row !important',
    },
  };

  // Base props that are always applied
  const baseProps = {
    value: momentValue,
    onChange,
    label,
    ampm,
    minutesStep,
    format: timeFormat,
    ...(minTime && { minTime }),
    ...(maxTime && { maxTime }),
    ...(name && { name }),
    ...(disabled !== undefined && { disabled }),
    ...(onAccept && { onAccept }),
    sx: combinedSx,
  };

  return (
    <CacheProvider value={cacheLtr}>
      <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
        <TimePicker
          {...(field || {})}
          {...baseProps}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slotProps={{
            textField: {
              ...(size && { size }),
              onClick: () => !disabled && setOpen(true),
              inputProps: { readOnly: true, style: { cursor: disabled ? 'default' : 'pointer' } },
            },
            dialog: { dir: 'ltr', sx: popupSx },
            popper: { dir: 'ltr', sx: popupSx },
            layout: { sx: popupSx },
            mobilePaper: { dir: 'ltr' },
          }}
        />
      </ThemeProvider>
    </CacheProvider>
  );
};

export default SharedMobileTimePicker;
